import React, { useState, useRef, useEffect } from "react";
import "./Chatbot.css";
import { Mic, Send, X, MessageCircle, MicOff, Calendar } from "lucide-react";
import Groq from "groq-sdk";

/* ==============================================================
   🔐 CONFIGURATION - Move to .env for production!
   ============================================================== */
const GROQ_API_KEY = "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";

// Initialize Groq client
const groq = new Groq({
  apiKey: GROQ_API_KEY,
  dangerouslyAllowBrowser: true, // Required for frontend usage
});

/* ==============================================================
   📋 DIETITIAN INFORMATION (From Server)
   ============================================================== */
const DIETITIAN_INFO = {
  profile: `Dietitian Karpagam is a clinical nutrition expert with 6+ years of experience in clinical and lifestyle nutrition. She holds an M.Sc. in Clinical Nutrition from Sri Ramachandra Institute of Higher Education and Research (SRIHER) and has worked at Apollo Hospitals, Greams Road, Chennai.

Her philosophy: "Wellness doesn't begin in the lab or gym — it begins on your plate."

Her approach is science-based, practical, and sustainable. No extremes, no deprivation — just conscious eating, consistent habits, and smart nutrition choices.`,

  experience: `She has 6+ years of experience in clinical and lifestyle nutrition, including training and work at Apollo Hospitals, Chennai. She's helped hundreds of clients achieve lasting results, including a 55-year-old prediabetic woman who has maintained normal A1c levels for over 3 years, and busy working professionals who achieved healthy weight loss without fad diets.`,

  education: `She holds an M.Sc. in Clinical Nutrition from Sri Ramachandra Institute of Higher Education and Research (SRIHER) and gained valuable clinical experience at Apollo Hospitals, Greams Road, Chennai.`,

  expertise: `She specializes in:
• Weight Management (Loss & Gain)
• Diabetes Care & Prediabetes
• Heart Health & Cardiac Nutrition
• PCOS & Women's Hormonal Health
• Maternal Health & Pregnancy Nutrition
• Chronic Disease Nutrition
• Lifestyle Nutrition
• Thyroid & Metabolic Health`,

  approach: `Her approach is simple: no extremes, no deprivation. Just conscious eating, consistent habits, and smart nutrition choices. She believes "wellness doesn't begin in the lab or gym — it begins on your plate!" All her plans are science-based, practical, sustainable, and based on home-cooked Indian food. No fad diets, meal replacements, powders, or pills.`,

  success: `She's helped hundreds of clients achieve lasting, sustainable results. Notable success stories include a 55-year-old prediabetic woman who has maintained normal A1c levels for over 3 years, and busy working professionals who achieved healthy weight loss without restrictive fad diets.`,
};

/* ==============================================================
   📄 FAQ DOCUMENT (From Server)
   ============================================================== */
const FAQ_DOCUMENT = `
Do you provide online consultations?
Yes. I work with clients across India through online consultations.
All sessions are personalised and include meal plans, guidance, and follow-ups based on your goals and lifestyle.

How does the consultation process work?
The process is simple:
1. You book a consultation
2. I assess your medical history, diet, and routine
3. I create a personalised diet plan
4. We stay connected through follow-ups and messages
You will receive clear instructions and support throughout the journey.

What kind of diet plans do you give?
My plans are:
• Practical and easy to follow
• Sustainable for long-term results
• Based on home-cooked Indian food
• Science-backed and evidence-based
No fad diets, meal replacements, powders or pills.

What conditions do you specialise in?
I work with a wide range of clients, including:
• Weight loss & weight gain
• PCOS & hormonal balance
• Diabetes & prediabetes
• Heart health & cardiac nutrition
• Thyroid & metabolic health
• Chronic disease management
• General wellness and lifestyle improvement

Can I meet you in person?
Yes, in-person consultations are available by prior appointment through referrals at partnered hospitals.
Online consultations are also available for convenience.

How often are follow-ups done?
Follow-ups depend on the program you choose.
Weekly or bi-weekly follow-ups are available for most programs to ensure accountability, motivation, and progress tracking.

Do you prescribe supplements?
Only when necessary, and based on your nutritional requirement.
I believe food is the first medicine — supplements are recommended only if needed.

Will I have to stop eating my favourite foods?
No.
My goal is to help you eat better, not less.
We focus on conscious eating, portion balance, and sustainable habits — not restriction.

How soon will I see results?
Results depend on your goals, medical condition, and consistency.
Most clients start noticing changes in 2–4 weeks, with sustainable transformation over 8–12 weeks.

Are your programs suitable for busy working individuals?
Yes.
I specialise in building simple, realistic plans that fit into hectic routines — without complicated cooking or fancy ingredients.

How do I get started?
Click on "Enquiry Form" and fill a short form.
You will receive a call/message to schedule your appointment.

Do you offer refunds?
Programs are personalised and involve dedicated time and planning.
Refunds are not offered, but I'm always available for guidance and support to ensure you get the best results.
`;

/* ==============================================================
   🔍 SERVICE KEYWORDS (From Server)
   ============================================================== */
const SERVICE_KEYWORDS = [
  "consultation", "consult", "appointment", "book", "meet",
  "diet", "plan", "meal", "food", "eating",
  "weight", "loss", "gain",
  "pcos", "pcod", "hormonal",
  "diabetes", "diabetic", "sugar", "prediabetes",
  "thyroid", "metabolic",
  "supplement", "medicine",
  "follow", "followup", "follow-up",
  "result", "how soon", "when",
  "online", "person", "visit",
  "refund", "payment", "cost", "price",
  "program", "suitable", "busy", "working",
  "favourite", "favorite", "restriction",
  "wellness", "health", "fitness",
  "karpagam", "experience", "qualification", "education",
  "apollo", "hospital", "background", "expertise",
  "approach", "method", "philosophy", "heart",
  "cardiac", "maternal", "pregnancy", "chronic",
  "success", "client", "testimonial", "helped",
  "who", "what", "where", "how", "why", "when",
  "her", "she", "your", "you", "about",
  "tell", "know", "info", "information", "details",
  "specialise", "specialize", "treat", "help",
  "work", "years", "trained", "studied", "degree"
];

/* ==============================================================
   🛠️ HELPER FUNCTIONS (From Server)
   ============================================================== */

// Check if query is related to the service
const isServiceRelated = (query) => {
  const lowerQuery = query.toLowerCase();
  return SERVICE_KEYWORDS.some(keyword => lowerQuery.includes(keyword));
};

// Get direct answer for common questions
const getDirectAnswer = (userMessage) => {
  const lowerMsg = userMessage.toLowerCase();

  // Who is Dietitian Karpagam?
  if ((lowerMsg.includes("who is") || lowerMsg.includes("who's")) &&
      (lowerMsg.includes("karpagam") || lowerMsg.includes("dietitian"))) {
    return DIETITIAN_INFO.profile;
  }

  // About her / Tell me about
  if ((lowerMsg.includes("about") || lowerMsg.includes("tell me")) &&
      (lowerMsg.includes("her") || lowerMsg.includes("karpagam") || lowerMsg.includes("dietitian"))) {
    return DIETITIAN_INFO.profile;
  }

  // Experience
  if (lowerMsg.includes("experience") ||
      lowerMsg.includes("how long") ||
      lowerMsg.includes("years of") ||
      lowerMsg.includes("background")) {
    return DIETITIAN_INFO.experience;
  }

  // Qualifications / Education
  if (lowerMsg.includes("qualification") ||
      lowerMsg.includes("education") ||
      lowerMsg.includes("degree") ||
      lowerMsg.includes("studied") ||
      lowerMsg.includes("training")) {
    return DIETITIAN_INFO.education;
  }

  // Expertise / Specialization
  if ((lowerMsg.includes("what") || lowerMsg.includes("which")) &&
      (lowerMsg.includes("condition") ||
       lowerMsg.includes("speciali") ||
       lowerMsg.includes("expertise") ||
       lowerMsg.includes("treat") ||
       lowerMsg.includes("help with"))) {
    return DIETITIAN_INFO.expertise;
  }

  // Approach / Method
  if (lowerMsg.includes("approach") ||
      lowerMsg.includes("method") ||
      lowerMsg.includes("philosophy") ||
      lowerMsg.includes("style")) {
    return DIETITIAN_INFO.approach;
  }

  // Success stories
  if (lowerMsg.includes("success") ||
      lowerMsg.includes("testimonial")) {
    return DIETITIAN_INFO.success;
  }

  return null;
};

// Get AI response using Groq
const getAIResponse = async (userMessage) => {
  const systemPrompt = `You are a helpful assistant for Dietitian Karpagam's nutrition consultation service.
Answer questions ONLY using the FAQ document below. Keep responses concise and helpful.

If the question is about Dietitian Karpagam's profile, background, experience, or expertise - those questions are handled separately, so just respond: "I can help you with questions about our consultation services, diet plans, and programs. For more about Dietitian Karpagam's background, please ask directly!"

FAQ DOCUMENT:
${FAQ_DOCUMENT}

Keep answers brief and friendly. If information is not in the FAQ, say: "I don't have specific details about that. Please contact us directly for more information."`;

  const completion = await groq.chat.completions.create({
    model: "llama-3.3-70b-versatile",
    messages: [
      { role: "system", content: systemPrompt },
      { role: "user", content: userMessage },
    ],
    temperature: 0.2,
    max_tokens: 300,
  });

  return completion.choices[0]?.message?.content;
};

/* ==============================================================
   🤖 CHATBOT COMPONENT
   ============================================================== */
const Chatbot = ({ onOpenEnquiryForm }) => {
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "👋 Hi! I'm your dietitian consultation assistant.\n\nI can help you with:\n✅ Weight Loss & Weight Gain\n✅ PCOS & Hormonal Balance\n✅ Diabetes & Prediabetes\n✅ Consultation Process\n✅ Diet Plans & Programs\n\nHow can I assist you today?",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState(null);

  // Refs
  const recognitionRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Booking keywords
  const bookingKeywords = [
    "book", "appointment", "consultation", "consult", "schedule",
    "meet", "session", "visit", "get started", "how to start",
    "enquiry", "enquire", "register", "sign up"
  ];

  // Initialize speech recognition
  useEffect(() => {
    if ("webkitSpeechRecognition" in window) {
      const recognition = new window.webkitSpeechRecognition();
      recognition.lang = "en-US";
      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onresult = (event) => {
        const speech = event.results[0][0].transcript;
        setInput((prev) => prev + (prev ? " " : "") + speech);
      };

      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsRecording(false);
        if (event.error === "no-speech") {
          setError("No speech detected. Please try again.");
          setTimeout(() => setError(null), 3000);
        }
      };

      recognition.onstart = () => setIsRecording(true);
      recognition.onend = () => setIsRecording(false);

      recognitionRef.current = recognition;
    }
  }, []);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen]);

  // Check if message should show booking button
  const shouldShowBookingButton = (userMessage) => {
    const lowerMsg = userMessage.toLowerCase();
    return bookingKeywords.some(keyword => lowerMsg.includes(keyword));
  };

  // Send message handler
  const sendMessage = async () => {
    if (!input.trim() || isTyping) return;

    const userMessage = {
      sender: "user",
      text: input.trim(),
      timestamp: new Date(),
    };

    const showBooking = shouldShowBookingButton(input.trim());
    const newMessages = [...messages, userMessage];
    const currentInput = input.trim();

    setMessages(newMessages);
    setInput("");
    setIsTyping(true);
    setError(null);

    try {
      let reply;

      // 1. Check if question is service-related
      if (!isServiceRelated(currentInput)) {
        reply = "I'm a specialized assistant for Dietitian Karpagam's consultation services. I can only answer questions about:\n\n✅ Personalized Diet Plans\n✅ Weight Loss & Weight Gain\n✅ PCOS & Hormonal Health\n✅ Diabetes & Heart Health\n✅ Consultation Process\n✅ Dietitian Karpagam's Background & Expertise\n\nPlease ask me something related to these topics! 😊";
      } else {
        // 2. Try direct answer first
        const directAnswer = getDirectAnswer(currentInput);
        
        if (directAnswer) {
          reply = directAnswer;
        } else {
          // 3. Use AI for response
          reply = await getAIResponse(currentInput);
        }
      }

      if (!reply) {
        throw new Error("No response generated");
      }

      const botMessage = {
        sender: "bot",
        text: reply,
        timestamp: new Date(),
        showBookingButton: showBooking,
      };

      setMessages([...newMessages, botMessage]);

    } catch (error) {
      console.error("Chat error:", error);
      
      let errorMsg = "I'm having trouble processing your request. Please try again.";
      
      if (error.status === 401) {
        errorMsg = "Service temporarily unavailable.";
      } else if (error.status === 429) {
        errorMsg = "Too many requests. Please wait a moment.";
      }

      setError(errorMsg);

      const errorMessage = {
        sender: "bot",
        text: "Sorry, I encountered an error. Please try again.",
        timestamp: new Date(),
      };
      setMessages([...newMessages, errorMessage]);

    } finally {
      setIsTyping(false);
    }
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Toggle voice recording
  const toggleRecording = () => {
    if (!recognitionRef.current) {
      setError("Voice input not supported in this browser. Try Chrome.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      try {
        recognitionRef.current.start();
        setError(null);
      } catch (err) {
        console.error("Recognition error:", err);
        setError("Could not start voice input.");
        setTimeout(() => setError(null), 3000);
      }
    }
  };

  // Clear conversation
  const clearConversation = () => {
    setMessages([
      {
        sender: "bot",
        text: "👋 Hi! I'm your dietitian consultation assistant.\n\nI can help you with:\n✅ Weight Loss & Weight Gain\n✅ PCOS & Hormonal Balance\n✅ Diabetes & Prediabetes\n✅ Consultation Process\n✅ Diet Plans & Programs\n\nHow can I assist you today?",
        timestamp: new Date(),
      },
    ]);
    setError(null);
  };

  // Format timestamp
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Handle booking button click
  const handleBookConsultation = () => {
    console.log("Book Consultation clicked - opening enquiry form");
    setIsOpen(false);
    if (onOpenEnquiryForm) {
      onOpenEnquiryForm();
    }
  };

  // Quick questions
  const quickQuestions = [
    "Do you provide online consultations?",
    "What conditions do you specialise in?",
    "How does the consultation process work?",
    "How do I book a consultation?",
  ];

  const handleQuickQuestion = (question) => {
    setInput(question);
    setTimeout(() => sendMessage(), 100);
  };

  /* ==============================================================
     🎨 RENDER
     ============================================================== */
  return (
    <>
      {/* Floating Chat Button */}
      <button
        className={`chatbot-button ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="chatbot-window">
          {/* Header */}
          <div className="chatbot-header">
            <div className="header-content">
              <span className="header-title">Dietitian Karpagam</span>
              <span className="header-subtitle">AI Assistant</span>
            </div>
            <button onClick={clearConversation} className="clear-btn">
              Clear
            </button>
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.map((msg, index) => (
              <div key={index}>
                <div className={`message-wrapper ${msg.sender}`}>
                  <div className={`message ${msg.sender}`}>
                    <p className="message-text">{msg.text}</p>
                    <span className="message-time">{formatTime(msg.timestamp)}</span>
                  </div>
                </div>

                {/* Booking Button */}
                {msg.sender === "bot" && msg.showBookingButton && (
                  <div className="booking-button-wrapper">
                    <button
                      onClick={handleBookConsultation}
                      className="booking-button"
                    >
                      <Calendar size={18} />
                      <span>Book a Consultation</span>
                    </button>
                  </div>
                )}
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="message-wrapper bot">
                <div className="message bot typing">
                  <span className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                </div>
              </div>
            )}

            {/* Quick Questions (only on first message) */}
            {messages.length === 1 && !isTyping && (
              <div className="quick-questions">
                <p className="quick-questions-title">Quick Questions:</p>
                {quickQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickQuestion(question)}
                    className="quick-question-btn"
                    disabled={isTyping}
                  >
                    {question}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Error Banner */}
          {error && (
            <div className="error-banner">
              <span>⚠️ {error}</span>
              <button onClick={() => setError(null)} className="error-close">
                ✕
              </button>
            </div>
          )}

          {/* Input Area */}
          <div className="chatbot-input">
            <input
              ref={inputRef}
              type="text"
              placeholder="Ask about consultations, diet plans, PCOS, diabetes..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={isTyping}
              className="chat-input-field"
            />

            <button
              onClick={toggleRecording}
              disabled={isTyping}
              className={`icon-btn mic-btn ${isRecording ? "recording" : ""}`}
              title={isRecording ? "Stop recording" : "Start voice input"}
            >
              {isRecording ? <MicOff size={18} /> : <Mic size={18} />}
            </button>

            <button
              onClick={sendMessage}
              disabled={!input.trim() || isTyping}
              className="send-btn"
              title="Send message"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;